import { useTranslation } from "react-i18next";
import type { Geofence, ThemeClasses } from "../types";
import { formatCoordinate } from "../utils/formatters";
import {
  getTypeColor,
  getTypeText,
  getTypeIcon,
  getStatusColorByName,
  getShapeText,
} from "../utils/helpers";
import { LINEAR_BUFFER_DEFAULT } from "../types";
import { useStatusesStore } from "../../../../../../store/admin/statusesStore";
import useIsMonitor from "../../../../../../hooks/useIsMonitor";
import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../../../components/base/CRUDTooltip";
import { DENSE_ROW, ICON_BTN, ICON_BTN_DANGER, STATUS_CHIP } from "../../../../shared";
interface GeofenceListRowProps {
  geofence: Geofence;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onEdit: (geofence: Geofence) => void;
  onViewMap: (geofence: Geofence) => void;
  onDelete?: (geofence: Geofence) => void;
}

export function GeofenceListRow({
  geofence,
  themeClasses,
  onEdit,
  onViewMap,
  onDelete,
}: GeofenceListRowProps) {
  const { t } = useTranslation("adminSections");
  const statuses = useStatusesStore((state) => state.statuses);
  const { isMonitor, isReadOnly } = useIsMonitor();
  // Obtener el status basado en status_id
  const status = statuses.find((s) => s.id === geofence.status_id);
  const statusName = status?.name || "Desconocido";
  const disableCRUD = useDisableCRUD();
  // Yard-owned geofences are system-managed: read-only here, edited from Patios.
  const isTruckYard =
    geofence.type === "truck_yard" || !!geofence.truck_yard_id;
  const neutralBtn = `${ICON_BTN} ${themeClasses.buttonHover} ${themeClasses.buttonText} ${themeClasses.buttonTextHover}`;
  const circle =
    geofence.coordinates && !Array.isArray(geofence.coordinates)
      ? geofence.coordinates
      : null;
  // Una sola métrica según la forma: radio, área o ancho de corredor.
  const metric =
    geofence?.shape === "circular"
      ? {
          label: t("geofences.details.radius", "Radio:"),
          value: circle ? `${circle.radius} m` : "N/A",
        }
      : geofence?.shape === "polygonal"
        ? {
            label: t("geofences.details.area", "Área:"),
            value: geofence.area
              ? `${(geofence.area / 1000000).toFixed(2)} m²`
              : "N/A",
          }
        : geofence?.shape === "linear"
          ? {
              label: t("geofences.details.corridorWidth", "Corredor:"),
              value: t("geofences.details.corridorWidthValue", {
                defaultValue: "±{{buffer}} m",
                buffer: geofence.buffer ?? LINEAR_BUFFER_DEFAULT,
              }),
            }
          : null;
  const coords =
    geofence.shape === "circular"
      ? circle
        ? `${formatCoordinate(circle.latitude)}, ${formatCoordinate(circle.longitude)}`
        : "N/A"
      : t("geofences.details.variable", "Variable");
  return (
    <div className={`${DENSE_ROW} ${themeClasses.cardBg} ${themeClasses.cardBorder}`}>
      <div
        className={`w-9 h-9 ${getTypeColor(
          geofence.type,
        )} rounded-lg flex items-center justify-center flex-shrink-0`}
      >
        <i className={`${getTypeIcon(geofence.type)} text-base`}></i>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className={`text-sm font-semibold ${themeClasses.text} truncate`} title={geofence.name}>
            {geofence.name}
          </h3>
          <span className={`${STATUS_CHIP} ${getStatusColorByName(statusName)}`}>
            {statusName}
          </span>
          {/* Mismo aviso que la tarjeta (GeofenceGridCard), compacto: la
              vista de lista no decía que la geocerca es de un patio. */}
          {isTruckYard && (
            <span
              title={t(
                "geofences.managedFromYard",
                "Esta geocerca pertenece a un patio de maniobras; gestiónala desde la sección de Patios de Maniobras.",
              )}
              className={`${STATUS_CHIP} gap-1 bg-teal-500/10 text-teal-600 dark:text-teal-300`}
            >
              <i className="ri-information-line" aria-hidden="true"></i>
              {t("geofences.yardBadge", "Patio")}
            </span>
          )}
        </div>
        <p className={`text-xs ${themeClasses.textSecondary} truncate`}>
          {getTypeText(geofence.type)} • {getShapeText(geofence.shape)}
        </p>
      </div>

      {metric && (
        <div className="hidden md:block w-24 shrink-0 min-w-0" title={`${metric.label} ${metric.value}`}>
          <p className={`text-[11px] ${themeClasses.textSecondary} truncate`}>{metric.label}</p>
          <p className={`text-sm ${themeClasses.text} truncate tabular-nums`}>{metric.value}</p>
        </div>
      )}
      <div className="hidden lg:block w-44 shrink-0 min-w-0" title={coords}>
        <p className={`text-[11px] ${themeClasses.textSecondary} truncate`}>
          {t("geofences.details.coordinates", "Coordenadas:")}
        </p>
        <p className={`text-xs font-mono ${themeClasses.text} truncate`}>{coords}</p>
      </div>
      <div
        className="hidden md:block w-20 shrink-0 min-w-0"
        title={t("geofences.stats.devices", "Dispositivos")}
      >
        <p className={`text-[11px] ${themeClasses.textSecondary} truncate`}>
          {t("geofences.stats.devices", "Dispositivos")}
        </p>
        <p className={`text-sm font-semibold ${themeClasses.text} tabular-nums`}>
          {geofence?.devices?.length ?? 0}
        </p>
      </div>

      <div className="flex items-center gap-0.5 shrink-0">
        {!isMonitor && !isReadOnly && (
          <CRUDTooltip>
            <button
              className={neutralBtn}
              onClick={() => onEdit(geofence)}
              disabled={disableCRUD}
              aria-label={t("geofences.actions.edit", "Editar")}
              title={t("geofences.actions.edit", "Editar")}
            >
              <i className="ri-edit-line"></i>
            </button>
          </CRUDTooltip>
        )}
        <button
          className={neutralBtn}
          onClick={() => onViewMap(geofence)}
          aria-label={t("geofences.actions.viewMap", "Ver mapa")}
          title={t("geofences.actions.viewMap", "Ver mapa")}
        >
          <i className="ri-map-2-line"></i>
        </button>
        {onDelete && !isMonitor && !isReadOnly && (
          <CRUDTooltip>
            <button
              className={ICON_BTN_DANGER}
              onClick={() => onDelete(geofence)}
              disabled={disableCRUD}
              aria-label={t("geofences.actions.delete", "Eliminar")}
              title={t("geofences.actions.delete", "Eliminar")}
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </CRUDTooltip>
        )}
      </div>
    </div>
  );
}
